'use client';

import type { ReactNode } from 'react';
import Image from 'next/image';
import { Box, Typography, type TypographyProps } from '@/primitives';

type ErrorCowCardProps = {
  /** Small uppercase badge rendered above the title, e.g. "404 · Animal not found". */
  label?: string;
  titleId?: string;
  titleComponent?: TypographyProps['component'];
  title: string;
  description: ReactNode;
  /** Action buttons rendered below the description. */
  actions?: ReactNode;
};

export function ErrorCowCard({
  label,
  titleId,
  titleComponent = 'h1',
  title,
  description,
  actions,
}: ErrorCowCardProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: '520px',
          padding: '32px',
          borderRadius: '12px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Image
            src="/images/error-cow.png"
            alt="Friendly cow illustration"
            width={200}
            height={150}
            priority
            style={{ width: '100%', maxWidth: '200px', height: 'auto' }}
          />
        </Box>
        {label && (
          <Typography
            variant="labelsmall"
            sx={{
              display: 'block',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'primary.main',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {label}
          </Typography>
        )}
        <Typography
          id={titleId}
          component={titleComponent}
          variant="headlinelarge"
          sx={{ color: 'text.primary', mb: 1 }}
        >
          {title}
        </Typography>
        <Typography
          variant="bodymedium"
          component="div"
          sx={{ color: 'text.secondary', mb: actions ? 3 : 0, lineHeight: 1.6 }}
        >
          {description}
        </Typography>
        {actions}
      </Box>
    </Box>
  );
}
